import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import {
  CurrentScopeFacade,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  IMarketingCampaign,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  snapshotDefined,
} from '@principle-theorem/shared';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CampaignSettingsFormComponent,
  IEditCampaignSettingsFormData,
} from '../campaign-settings-form/campaign-settings-form.component';
import { CampaignTemplateSelectorComponent } from '../campaign-template-selector/campaign-template-selector.component';
import {
  IMarketingCampaignTemplate,
  MarketingCampaignTemplate,
} from '../marketing-campaign-templates/marketing-campaign-template';
import { getDefaultMarketingCampaignTemplate } from '../marketing-campaign-templates/marketing-campaign-templates';

export interface IAddCampaignDialogResponse {
  campaignRef: DocumentReference<IMarketingCampaign>;
}

@Component({
  selector: 'pr-add-campaign-dialog',
  templateUrl: './add-campaign-dialog.component.html',
  styleUrl: './add-campaign-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    CampaignTemplateSelectorComponent,
    CampaignSettingsFormComponent,
    MatStepperModule,
  ],
})
export class AddCampaignDialogComponent {
  private _organisation = inject(OrganisationService);
  private _scope = inject(CurrentScopeFacade);
  private _dialogRef =
    inject<
      MatDialogRef<AddCampaignDialogComponent, IAddCampaignDialogResponse>
    >(MatDialogRef);
  settingsValue = signal<IEditCampaignSettingsFormData | undefined>(undefined);
  templateValue = signal<IMarketingCampaignTemplate | undefined>(undefined);

  constructor() {
    combineLatest([this._organisation.region$, this._scope.currentPractice$])
      .pipe(
        map(([region, practice]) =>
          practice && region
            ? getDefaultMarketingCampaignTemplate(practice.ref, region)
            : undefined
        ),
        filterUndefined(),
        takeUntilDestroyed()
      )
      .subscribe((template) => void this.selectTemplate(template));
  }

  async selectTemplate(
    campaignTemplate: IMarketingCampaignTemplate
  ): Promise<void> {
    this.templateValue.set(campaignTemplate);
    const practiceRef =
      this.settingsValue()?.practiceRef ??
      (await this._resolveDefaultPractice());
    this.settingsValue.set({
      name: campaignTemplate.campaign.name,
      type: campaignTemplate.campaign.type,
      practiceRef,
    });
  }

  async submit(): Promise<void> {
    const settings = this.settingsValue();
    const template = this.templateValue();
    if (!settings || !template) {
      return;
    }
    const staffer = await snapshotDefined(this._organisation.staffer$);
    const campaignRef =
      await MarketingCampaignTemplate.createCampaignFromTemplate(
        template,
        settings,
        staffer
      );
    this._dialogRef.close({ campaignRef });
  }

  private async _resolveDefaultPractice(): Promise<
    DocumentReference<IPractice>
  > {
    const practice = await snapshotDefined(this._scope.currentPractice$);
    return practice.ref;
  }
}
